import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderTree, Plus, Edit2, Trash2, Building, Folder } from "lucide-react";

const TYPE_ICON = { hcd_root: FolderTree, entity: Building, folder: Folder };
const TYPE_LABEL = { hcd_root: "HCD Root", entity: "Entity / LLC", folder: "Folder" };

export default function HierarchyAdmin() {
  const [flat, setFlat] = useState([]);
  const [tree, setTree] = useState([]);
  const [dialog, setDialog] = useState(null);   // { mode: create|edit, parent_id, node }

  const load = async () => {
    try {
      const r = await api.get("/hierarchy/tree");
      setFlat(r.data.flat || []);
      setTree(r.data.tree || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this node?")) return;
    try {
      await api.delete(`/hierarchy/nodes/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const renderTree = (nodes, depth = 0) => nodes.map(n => {
    const Icon = TYPE_ICON[n.entity_type] || Folder;
    return (
      <div key={n.id}>
        <div className="flex items-center gap-2 py-1.5 hover:bg-slate-50 rounded px-2" style={{ paddingLeft: 12 + depth * 20 }} data-testid={`hier-node-${n.id}`}>
          <Icon className={`w-4 h-4 ${n.entity_type === "hcd_root" ? "text-[#005289]" : n.entity_type === "entity" ? "text-amber-600" : "text-slate-500"}`} />
          <span className="text-sm font-medium">{n.name}</span>
          <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{TYPE_LABEL[n.entity_type]}</span>
          {n.department && <span className="text-[10px] text-slate-500">· {n.department}</span>}
          <div className="ml-auto flex gap-1">
            {n.entity_type !== "hcd_root" && (
              <button className="text-xs p-1 hover:bg-slate-200 rounded" onClick={() => setDialog({ mode: "edit", node: n })} data-testid={`hier-edit-${n.id}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button className="text-xs p-1 hover:bg-slate-200 rounded text-blue-700" onClick={() => setDialog({ mode: "create", parent_id: n.id, parent_type: n.entity_type })} data-testid={`hier-add-${n.id}`}>
              <Plus className="w-3.5 h-3.5" />
            </button>
            {n.entity_type !== "hcd_root" && (
              <button className="text-xs p-1 hover:bg-red-50 rounded text-red-600" onClick={() => del(n.id)} data-testid={`hier-del-${n.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {n.children && renderTree(n.children, depth + 1)}
      </div>
    );
  });

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="hierarchy-admin">
      <div className="flex items-center gap-3 mb-6">
        <FolderTree className="w-6 h-6 text-[#005289]" />
        <div>
          <h1 className="text-xl font-bold">Repository Hierarchy</h1>
          <p className="text-sm text-slate-500">HCD → corporate entities / LLCs → folders. Configure the structure used to file contracts.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        {tree.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Loading…</div>
        ) : renderTree(tree)}
      </div>

      {dialog && <NodeDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function NodeDialog({ dialog, onClose, onSaved }) {
  const isEdit = dialog.mode === "edit";
  const [name, setName] = useState(isEdit ? dialog.node.name : "");
  const [department, setDepartment] = useState(isEdit ? (dialog.node.department || "") : "");
  const [entityType, setEntityType] = useState(isEdit ? dialog.node.entity_type : (dialog.parent_type === "hcd_root" ? "entity" : "folder"));

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      if (isEdit) {
        await api.patch(`/hierarchy/nodes/${dialog.node.id}`, { name, department });
      } else {
        await api.post("/hierarchy/nodes", { name, parent_id: dialog.parent_id, entity_type: entityType, department });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="hier-dialog">
        <h3 className="text-lg font-bold mb-4">{isEdit ? "Edit node" : "Create node"}</h3>
        <div className="space-y-3">
          {!isEdit && (
            <div>
              <label className="text-xs font-bold block mb-1">Type</label>
              <select className="w-full h-9 px-3 text-sm border border-slate-200 rounded" value={entityType} onChange={e => setEntityType(e.target.value)} data-testid="dialog-type">
                <option value="entity">Entity / LLC</option>
                <option value="folder">Folder</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold block mb-1">Name</label>
            <input className="w-full h-9 px-3 text-sm border border-slate-200 rounded" value={name} onChange={e => setName(e.target.value)} data-testid="dialog-name" />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1">Department (optional)</label>
            <input className="w-full h-9 px-3 text-sm border border-slate-200 rounded" value={department} onChange={e => setDepartment(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="text-sm px-4 py-2 border border-slate-300 rounded" onClick={onClose}>Cancel</button>
          <button className="text-sm px-4 py-2 bg-[#005289] text-white rounded" onClick={save} data-testid="dialog-save">Save</button>
        </div>
      </div>
    </div>
  );
}
